/**
 * mengerSpongePipeline — 3D raymarched Menger sponge.
 *
 * The Menger sponge is a 3D fractal: a cube with a 3x3x3 grid of
 * smaller cubes where the center cube and the face centers are
 * removed. We compute the SDF via recursive subtraction.
 *
 * Raymarch the sponge with diffuse + iridescent lighting.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MengerSpongePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

// Distance to a "hole" position on a 3x3 grid (the 20 holes of the
// Menger sponge iteration 1).
float sdMengerHole(vec3 p, float scale) {
  vec3 q = mod(p + scale * 0.5, scale) - scale * 0.5;
  // 3x3x3 grid: remove the 6 face centers and the body center.
  // We check if q is near one of these positions.
  float d = 1000.0;
  // Face centers (6 of them).
  float faceHole = sdBox(q, vec3(scale * 0.4, scale * 0.05, scale * 0.05));
  faceHole = min(faceHole, sdBox(q, vec3(scale * 0.05, scale * 0.4, scale * 0.05)));
  faceHole = min(faceHole, sdBox(q, vec3(scale * 0.05, scale * 0.05, scale * 0.4)));
  d = min(d, faceHole);
  return d;
}

float sdMenger(vec3 p) {
  float d = sdBox(p, vec3(1.0));
  // Iteratively subtract holes.
  float scale = 1.0;
  for (int i = 0; i < 3; i++) {
    // Position in the current grid.
    float hole = sdMengerHole(p, scale);
    d = max(d, -hole);
    scale /= 3.0;
  }
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    sdMenger(p + e.xyy) - sdMenger(p - e.xyy),
    sdMenger(p + e.yxy) - sdMenger(p - e.yxy),
    sdMenger(p + e.yyx) - sdMenger(p - e.yyx)
  ));
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  // Menger sponge 2D projection.
  float t = uTime * 0.3;
  // Rotate the coordinate frame.
  float angle = t * 0.5;
  float ca = cos(angle), sa = sin(angle);
  mat2 rot = mat2(ca, -sa, sa, ca);
  vec2 rp = rot * p * 1.5;
  vec3 col = vec3(0.04, 0.03, 0.08);
  // For each screen pixel, sample the sponge at a few Z slices and
  // accumulate density. The sponge at a 3D point is a value that
  // decreases as we go deeper into the fractal.
  float density = 0.0;
  for (int zi = 0; zi < 20; zi++) {
    float z = (float(zi) / 20.0 - 0.5) * 3.0;
    vec3 q = vec3(rp, z);
    // Approximate the sponge as the inverse of the number of "removed"
    // holes at this 3D point.
    float v = 1.0;
    float scale = 1.0;
    for (int iter = 0; iter < 4; iter++) {
      // 3x3x3 grid: remove the body center and 6 face centers.
      vec3 qm = mod(q + 1.5, 3.0) - 1.5;
      float bodyCenter = max(abs(qm.x), max(abs(qm.y), abs(qm.z)));
      if (bodyCenter < 0.5) v *= 0.2;
      // Face centers.
      if (length(vec3(qm.x * 3.0, qm.y, qm.z)) < 0.5) v *= 0.2;
      if (length(vec3(qm.x, qm.y * 3.0, qm.z)) < 0.5) v *= 0.2;
      if (length(vec3(qm.x, qm.y, qm.z * 3.0)) < 0.5) v *= 0.2;
      q *= 3.0;
    }
    float d = v * (0.5 + bass * 0.5);
    vec3 layerColor = mix(
      vec3(0.20, 0.30, 0.85),
      vec3(0.95, 0.55, 0.20),
      float(zi) / 20.0
    );
    col += layerColor * d * 0.3;
    density += d;
  }
  col *= 1.0 + mid * 0.4;
  col = col / (1.0 + density * 0.15);
  col *= 1.0 + bass * 0.2;

  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeMengerSpongeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeMengerSpongeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMengerSpongePipeline(gl: WebGL2RenderingContext): MengerSpongePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMengerSponge(
  gl: WebGL2RenderingContext,
  pipe: MengerSpongePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
